import { useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Box } from "@mui/material";

import {
  RefreshButton,
  UploadButton,
} from "@app/analytics/MeetingReports/styles";
import useMeetingReportsStore from "@app/analytics/stores/useMeetingReportsStore";
import useAuth from "@app/common/auth/useAuth";
import ROUTES from "@app/common/routes";

import { DesktopOnlyRow } from "@rui/Stack";

import useUnseenStore from "./stores/unseen";
import Tabs from "../components/Tabs";
import useRollupContext from "../hooks/meetops/useRollupContext";
import { RollupFilterOption } from "../providers/RollupContext";

interface MeetingListTab {
  id: string;
  label: string;
  getLabel: (
    t: RollupFilterOption,
    u: RollupFilterOption,
    onlyMe: boolean
  ) => string;
  path: string;
  disabled: boolean;
  getDisabled: (onlyMe: boolean) => boolean;
  testId: string;
}

const MEETINGS_LIST_TABS: MeetingListTab[] = [
  {
    id: "meetings",
    label: "Meetings",
    getLabel: (t, u, onlyMe) => {
      if (onlyMe) return "My reports";
      return t ? `${u.id === "ALL" ? t.label : u.label}'s reports` : "Reports";
    },
    path: "",
    disabled: false,
    getDisabled: () => false,
    testId: "meeting-list-tab-meetings",
  },
  {
    id: "sharedWithMe",
    label: "Shared with me",
    path: "shared",
    getLabel: () => "Shared with me",
    disabled: false,
    getDisabled: (onlyMe) => !onlyMe,
    testId: "meeting-list-tab-shared",
  },
  {
    id: "incomplete",
    label: "Incomplete",
    path: "incomplete",
    getLabel: () => "Incomplete",
    disabled: false,
    getDisabled: (onlyMe) => !onlyMe,
    testId: "meeting-list-tab-incomplete",
  },
];

const MeetingReportsTabs = ({
  onUploadClick,
}: {
  onUploadClick: () => void;
}) => {
  const { unseenIncompleteCount, unseenSharedWithMeCount } = useUnseenStore();

  const {
    teamFilter,
    usersFilter,
    isLoading: isRollupLoading,
  } = useRollupContext();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isFetching = useMeetingReportsStore((state) => state.isFetching);

  const onlyMe = isRollupLoading || user?.id === usersFilter.id;

  const getTabs = () => {
    return MEETINGS_LIST_TABS.map((t) => ({
      ...t,
      label: t.getLabel(teamFilter, usersFilter, onlyMe),
      disabled: t.getDisabled(onlyMe),
      notificationCount:
        t.id === "sharedWithMe"
          ? unseenSharedWithMeCount
          : t.id === "incomplete"
            ? unseenIncompleteCount
            : 0,
    }));
  };

  const handleRefresh = () => {
    if (isFetching) return;
    queryClient.invalidateQueries({ queryKey: ["sessionsList"] });
    queryClient.invalidateQueries({ queryKey: ["topics"] });
  };

  const tabsToRender = useMemo(
    () => getTabs(),
    [
      onlyMe,
      teamFilter,
      usersFilter,
      unseenIncompleteCount,
      unseenSharedWithMeCount,
    ]
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Tabs
        variant="scrollable"
        tabs={tabsToRender}
        defaultRoute={ROUTES.MEETING_REPORTS}
        sx={{
          mt: { xxs: 1, mobile: 2 },
          "& .MuiTab-root": {
            minWidth: 0,
            "&.Mui-disabled": { display: "none" },
          },
        }}
      />
      <DesktopOnlyRow
        sx={{
          right: "20px",
          bottom: "14px",
          zIndex: 3,
          position: "absolute",
        }}
        spacing={2}
      >
        <RefreshButton onClick={handleRefresh} refreshing={isFetching}>
          Refresh
        </RefreshButton>
        <UploadButton onClick={onUploadClick}>Upload</UploadButton>
      </DesktopOnlyRow>
    </Box>
  );
};

export default MeetingReportsTabs;
